import { useCallback, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'wouter'
import { isKnownRoutePath } from '@/app/routeNavigation'

const KEY = '__welearnNavigation'
type Snapshot = { id?: string; href?: string; ariaLabel?: string; text?: string }
type Entry = { version: 1; stackId: string; entryId: string; previousEntryId?: string; pathname: string; scrollX?: number; scrollY?: number; focus?: Snapshot }
type State = Record<string, unknown> & { [KEY]?: Entry }
let lastAction: 'push' | 'replace' | 'pop' | 'initial' = 'initial'
let nativeReplaceState = window.history.replaceState.bind(window.history)

const id = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
const currentState = (): State => { const raw: unknown = window.history.state; return raw && typeof raw === 'object' ? raw as State : {} }
const valid = (value: unknown): value is Entry => Boolean(value && typeof value === 'object' && (value as Entry).version === 1 && typeof (value as Entry).stackId === 'string' && typeof (value as Entry).entryId === 'string')
const pathFromURL = (url: string | URL | null | undefined) => { if (!url) return `${window.location.pathname}${window.location.search}${window.location.hash}`; const parsed = new URL(String(url), window.location.href); return `${parsed.pathname}${parsed.search}${parsed.hash}` }
const snapshot = (): Snapshot | undefined => { const element = document.activeElement; if (!(element instanceof HTMLElement) || element === document.body) return undefined; return { id: element.id || undefined, href: element instanceof HTMLAnchorElement ? element.getAttribute('href') ?? undefined : undefined, ariaLabel: element.getAttribute('aria-label') ?? undefined, text: element.textContent?.trim().slice(0, 120) || undefined } }

export function saveNavigationSnapshot() {
  const entry = currentState()[KEY]
  if (!valid(entry)) return
  nativeReplaceState({ ...currentState(), [KEY]: { ...entry, scrollX: window.scrollX, scrollY: window.scrollY, focus: snapshot() } }, '')
}

function restore(value?: Snapshot) {
  let target = value?.id ? document.getElementById(value.id) : null
  if (!target && value?.href) target = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]')).find((item) => item.getAttribute('href') === value.href) ?? null
  if (!target && value?.ariaLabel) target = Array.from(document.querySelectorAll<HTMLElement>('[aria-label]')).find((item) => item.getAttribute('aria-label') === value.ariaLabel) ?? null
  if (!target && value?.text) target = Array.from(document.querySelectorAll<HTMLElement>('a[href],button')).find((item) => item.textContent?.trim().slice(0, 120) === value.text) ?? null
  target ??= document.querySelector<HTMLElement>('[data-route-autofocus],h1,#main-content')
  if (target && !target.matches('input,textarea,select,button,a[href]') && target.tabIndex < 0) target.tabIndex = -1
  target?.focus({ preventScroll: true })
}

export function RouteNavigationProvider() {
  const [pathname] = useLocation()
  const active = useRef<Entry | undefined>(undefined)
  useLayoutEffect(() => {
    const originalPush = window.history.pushState.bind(window.history)
    const originalReplace = window.history.replaceState.bind(window.history)
    nativeReplaceState = originalReplace
    const initial = currentState()[KEY]
    if (valid(initial)) active.current = initial
    else {
      const entry: Entry = { version: 1, stackId: id(), entryId: id(), pathname: `${window.location.pathname}${window.location.search}${window.location.hash}` }
      originalReplace({ ...currentState(), [KEY]: entry }, '')
      active.current = entry
    }
    window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
      saveNavigationSnapshot()
      const previous = active.current
      const entry: Entry = { version: 1, stackId: previous?.stackId ?? id(), entryId: id(), previousEntryId: previous?.entryId, pathname: pathFromURL(url) }
      lastAction = 'push'; active.current = entry; originalPush({ ...(data && typeof data === 'object' ? data as object : {}), [KEY]: entry }, unused, url)
    }
    window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
      saveNavigationSnapshot()
      const previous = active.current
      const entry: Entry = { version: 1, stackId: previous?.stackId ?? id(), entryId: id(), previousEntryId: previous?.previousEntryId, pathname: pathFromURL(url) }
      lastAction = 'replace'; active.current = entry; originalReplace({ ...(data && typeof data === 'object' ? data as object : {}), [KEY]: entry }, unused, url)
    }
    const onPop = () => { lastAction = 'pop'; const entry = currentState()[KEY]; active.current = valid(entry) ? entry : undefined }
    window.addEventListener('popstate', onPop)
    return () => { window.history.pushState = originalPush; window.history.replaceState = originalReplace; window.removeEventListener('popstate', onPop) }
  }, [])
  useLayoutEffect(() => {
    const entry = currentState()[KEY]
    if (lastAction === 'pop' && valid(entry)) window.requestAnimationFrame(() => { window.scrollTo({ left: entry.scrollX ?? 0, top: entry.scrollY ?? 0, behavior: 'instant' }); window.requestAnimationFrame(() => restore(entry.focus)) })
  }, [pathname])
  return null
}

export function useAppBack(fallback: string) {
  const [, navigate] = useLocation()
  return useCallback(() => { saveNavigationSnapshot(); const entry = currentState()[KEY]; if (valid(entry) && entry.previousEntryId) window.history.back(); else navigate(fallback, { replace: true }) }, [fallback, navigate])
}

export function wasPopNavigation() { return lastAction === 'pop' }

export function normalizeInternalReturnTo(value: unknown, fallback = '/') {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return fallback
  try { const url = new URL(value, window.location.origin); return url.origin === window.location.origin && isKnownRoutePath(url.pathname) && url.pathname !== '/login' ? `${url.pathname}${url.search}${url.hash}` : fallback } catch { return fallback }
}
